import { useEffect } from 'react';
import type { Source } from '@/lib/brandSources';
import { manifestUrlFor } from '@/lib/api';
import { useBrand } from '@/lib/brand';

/**
 * The home-screen app follows the org: when the signed-in resident's office
 * brand or a link's org hint is known, the page links that org's manifest
 * (`GET /api/public/manifest/:handle`, fetched with `crossorigin`, no
 * credentials) and its app icon as the apple-touch-icon. Otherwise the
 * shipped TendedLoop manifest and icon stay.
 */

/** The org whose app this page offers: the session's, else the hint's; only with a brand. */
export function appHandleOf(s: { session: Source; hint: Source }): string | null {
  for (const src of [s.session, s.hint]) {
    if (src.state === 'ready' && src.brand && src.handle) return src.handle;
  }
  return null;
}

/** The session brand's own app icon (the hint's comes from its manifest). */
function sessionIcon(s: { session: Source }): string | null {
  return s.session.state === 'ready' && s.session.handle ? (s.session.brand?.appIconUrl ?? null) : null;
}

interface Defaults {
  manifest: string | null;
  touchIcon: string | null;
}
let defaults: Defaults | null = null;

function link(rel: string): HTMLLinkElement | null {
  return document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
}

/** Point the manifest and touch icon at an org's app, or back at TendedLoop's (null). */
export function setAppIdentity(handle: string | null, icon: string | null) {
  defaults ??= {
    manifest: link('manifest')?.getAttribute('href') ?? null,
    touchIcon: link('apple-touch-icon')?.getAttribute('href') ?? null,
  };
  let manifest = link('manifest');
  if (!manifest) {
    manifest = document.createElement('link');
    manifest.rel = 'manifest';
    document.head.appendChild(manifest);
  }
  if (handle) {
    manifest.setAttribute('crossorigin', 'anonymous');
    manifest.setAttribute('href', manifestUrlFor(handle));
  } else {
    manifest.removeAttribute('crossorigin');
    if (defaults.manifest) manifest.setAttribute('href', defaults.manifest);
    else manifest.remove();
  }

  let touch = link('apple-touch-icon');
  const href = handle && icon ? icon : defaults.touchIcon;
  if (!href) {
    touch?.remove();
    return;
  }
  if (!touch) {
    touch = document.createElement('link');
    touch.rel = 'apple-touch-icon';
    document.head.appendChild(touch);
  }
  touch.setAttribute('href', href);
}

/** The first icon of an org's manifest (a simple GET, no credentials), or null. */
async function manifestIcon(handle: string): Promise<string | null> {
  try {
    const res = await fetch(manifestUrlFor(handle), { credentials: 'omit' });
    if (!res.ok) return null;
    const m = (await res.json()) as { icons?: { src?: unknown }[] };
    const src = m.icons?.[0]?.src;
    return typeof src === 'string' && /^https?:\/\//.test(src) ? src : null;
  } catch {
    return null;
  }
}

/** Keep the page's app identity in step with the brand (mounted once, in App). */
export function useAppIdentity() {
  const handle = useBrand(appHandleOf);
  const icon = useBrand(sessionIcon);
  useEffect(() => {
    let live = true;
    setAppIdentity(handle, icon);
    if (handle && !icon) {
      void manifestIcon(handle).then((src) => {
        if (live && src) setAppIdentity(handle, src);
      });
    }
    return () => {
      live = false;
    };
  }, [handle, icon]);
}
